import React, { useEffect, useRef, useState } from "react";

import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@material-ui/core";
import html2pdf from "html2pdf.js";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { mediaFileName } from "../../utils/downloadMedia";

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(blob);
});

const fetchMediaBlob = async (url) => {
  const response = await api.get(url, { responseType: "blob" });
  return new Blob([response.data], {
    type: response.headers?.["content-type"] || response.data?.type || "application/octet-stream",
  });
};

const imageDataUrl = async (url) => blobToDataUrl(await fetchMediaBlob(url));

const videoThumbnailDataUrl = async (url) => {
  const blob = await fetchMediaBlob(url);
  const objectUrl = URL.createObjectURL(blob);

  try {
    const video = document.createElement("video");
    video.muted = true;
    video.playsInline = true;
    video.preload = "metadata";
    video.src = objectUrl;

    await new Promise((resolve, reject) => {
      const timeout = window.setTimeout(() => reject(new Error("VIDEO_THUMBNAIL_TIMEOUT")), 10000);
      video.onloadeddata = () => {
        window.clearTimeout(timeout);
        resolve();
      };
      video.onerror = () => {
        window.clearTimeout(timeout);
        reject(new Error("VIDEO_THUMBNAIL_ERROR"));
      };
    });

    if (Number.isFinite(video.duration) && video.duration > 0.2) {
      video.currentTime = Math.min(0.2, video.duration / 2);
      await new Promise((resolve) => {
        const timeout = window.setTimeout(resolve, 1200);
        video.onseeked = () => {
          window.clearTimeout(timeout);
          resolve();
        };
      });
    }

    const width = video.videoWidth || 640;
    const height = video.videoHeight || 360;
    const maxWidth = 620;
    const scale = Math.min(1, maxWidth / width);
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(width * scale));
    canvas.height = Math.max(1, Math.round(height * scale));
    const context = canvas.getContext("2d");
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", 0.86);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

const formatDateTime = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.toLocaleDateString("pt-BR")} ${date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`;
};

const extractAudioDuration = (message) => {
  try {
    const data = typeof message.dataJson === "string" ? JSON.parse(message.dataJson) : message.dataJson;
    const seconds = Number(
      data?.message?.audioMessage?.seconds ||
      data?.audioMessage?.seconds ||
      data?.message?.pttMessage?.seconds ||
      0
    );
    if (!seconds) return "";
    const minutes = Math.floor(seconds / 60);
    const rest = String(Math.floor(seconds % 60)).padStart(2, "0");
    return `${minutes}:${rest}`;
  } catch (_) {
    return "";
  }
};


const parseVcards = (body) => {
  const text = String(body || "");
  if (!/BEGIN:VCARD/i.test(text)) return [];

  const cards = text.match(/BEGIN:VCARD[\s\S]*?END:VCARD/gi) || [text];
  return cards.map((card) => {
    const name = (/^FN:(.*)$/im.exec(card)?.[1] || /^N:[^;]*;([^;]*)/im.exec(card)?.[1] || "Contato").trim();
    const waid = /waid=(\d+)/i.exec(card)?.[1];
    const tel = /TEL[^:]*:\+?([^\r\n]+)/i.exec(card)?.[1];
    const number = String(waid || tel || "").replace(/[^0-9+]/g, "");
    return { name, number };
  }).filter((item) => item.name || item.number);
};

const contactHtml = (message) => {
  const contacts = parseVcards(message.body);
  if (!contacts.length) return "";
  return contacts.map((item) => `
    <div class="pdf-contact-card">
      <div class="pdf-contact-icon">👤</div>
      <div>
        <div class="pdf-contact-name">${escapeHtml(item.name || "Contato")}</div>
        ${item.number ? `<div class="pdf-contact-number">📱 ${escapeHtml(item.number.startsWith("+") ? item.number : `+${item.number}`)}</div>` : ""}
      </div>
    </div>
  `).join("");
};

const mediaHtml = async (message) => {
  if (!message.mediaUrl) return "";

  const type = String(message.mediaType || "").toLowerCase();
  const filename = mediaFileName(message.mediaUrl);

  if (["image", "sticker", "gif"].includes(type)) {
    try {
      const src = await imageDataUrl(message.mediaUrl);
      return `<div class="pdf-media"><img src="${src}" alt="${escapeHtml(filename)}" /></div>`;
    } catch (_) {
      return `<div class="pdf-file">🖼️ Imagem: ${escapeHtml(filename)}</div>`;
    }
  }

  if (type === "video") {
    try {
      const src = await videoThumbnailDataUrl(message.mediaUrl);
      return `<div class="pdf-media pdf-video"><img src="${src}" alt="Vídeo" /><div class="pdf-media-label">▶ Vídeo — ${escapeHtml(filename)}</div></div>`;
    } catch (_) {
      return `<div class="pdf-file">🎥 Vídeo: ${escapeHtml(filename)}</div>`;
    }
  }

  if (type === "audio") {
    const duration = extractAudioDuration(message);
    return `<div class="pdf-file">🎤 Áudio${duration ? ` — ${duration}` : ""}${filename ? ` — ${escapeHtml(filename)}` : ""}</div>`;
  }

  return `<div class="pdf-file">📎 ${escapeHtml(filename || message.body || "Documento")}</div>`;
};

const buildConversationElement = async ({ ticket, messages }) => {
  const container = document.createElement("div");
  container.className = "pdf-conversation";

  const contactName = ticket?.contact?.name || "Contato";
  const contactNumber = ticket?.contact?.number || "";

  let html = `
    <style>
      .pdf-conversation { font-family: Arial, sans-serif; background:#efeae2; color:#222; width:760px; padding:0; }
      .pdf-header { background:#075e54; color:#fff; padding:16px 20px; }
      .pdf-header-title { font-size:18px; font-weight:700; }
      .pdf-header-sub { font-size:12px; opacity:.9; margin-top:4px; }
      .pdf-chat { padding:18px 20px 30px; }
      .pdf-day { text-align:center; margin:12px 0; font-size:11px; color:#667781; }
      .pdf-day span { background:#d9fdd3; border-radius:7px; padding:5px 10px; }
      .pdf-row { display:flex; margin:5px 0; page-break-inside:avoid; break-inside:avoid; }
      .pdf-row.in { justify-content:flex-start; }
      .pdf-row.out { justify-content:flex-end; }
      .pdf-bubble { max-width:68%; border-radius:8px; padding:8px 9px 6px; box-shadow:0 1px 1px rgba(0,0,0,.15); }
      .pdf-row.in .pdf-bubble { background:#fff; border-top-left-radius:0; }
      .pdf-row.out .pdf-bubble { background:#d9fdd3; border-bottom-right-radius:0; }
      .pdf-sender { font-size:11px; font-weight:700; margin-bottom:4px; color:#0b6bcb; }
      .pdf-row.out .pdf-sender { color:#1b5e20; }
      .pdf-text { font-size:13px; white-space:pre-wrap; overflow-wrap:anywhere; }
      .pdf-time { font-size:9px; color:#667781; text-align:right; margin-top:4px; }
      .pdf-media img { display:block; max-width:300px; max-height:300px; width:auto; height:auto; object-fit:contain; border-radius:6px; }
      .pdf-video { position:relative; }
      .pdf-media-label { font-size:10px; color:#667781; margin-top:4px; }
      .pdf-file { background:rgba(255,255,255,.65); border:1px solid rgba(0,0,0,.1); border-radius:6px; padding:10px; font-size:12px; }
      .pdf-contact-card { display:flex; align-items:center; gap:10px; min-width:220px; padding:10px; border:1px solid rgba(0,0,0,.08); border-radius:7px; background:rgba(255,255,255,.72); }
      .pdf-contact-icon { width:34px; height:34px; display:flex; align-items:center; justify-content:center; border-radius:50%; background:#e5e7eb; font-size:18px; }
      .pdf-contact-name { font-size:13px; font-weight:700; }
      .pdf-contact-number { font-size:11px; color:#667781; margin-top:3px; }
      .pdf-deleted { font-style:italic; color:#777; }
    </style>
    <div class="pdf-header">
      <div class="pdf-header-title">${escapeHtml(contactName)}</div>
      <div class="pdf-header-sub">${escapeHtml(contactNumber)}${ticket?.id ? ` • Atendimento #${ticket.id}` : ""}</div>
    </div>
    <div class="pdf-chat">
  `;

  let lastDay = "";

  for (const message of messages) {
    const date = new Date(message.createdAt);
    const day = Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString("pt-BR");
    if (day && day !== lastDay) {
      html += `<div class="pdf-day"><span>${escapeHtml(day)}</span></div>`;
      lastDay = day;
    }

    const media = await mediaHtml(message);
    const isContactMessage = ["contactMessage", "contactsArrayMessage", "vcard"].includes(String(message.mediaType || ""));
    const contactCard = isContactMessage || /BEGIN:VCARD/i.test(String(message.body || ""))
      ? contactHtml(message)
      : "";
    const body = String(message.body || "");
    const filename = message.mediaUrl ? mediaFileName(message.mediaUrl) : "";
    const bodyIsOnlyFileName = filename && body.trim() === filename.trim();
    const visibleBody = message.isDeleted
      ? "Mensagem apagada"
      : (contactCard ? "" : (bodyIsOnlyFileName ? "" : body));

    const sender = message.fromMe
      ? (ticket?.user?.name || "Atendente")
      : (message?.contact?.name || contactName);

    html += `
      <div class="pdf-row ${message.fromMe ? "out" : "in"}">
        <div class="pdf-bubble">
          <div class="pdf-sender">${escapeHtml(sender)}</div>
          ${media}
          ${contactCard}
          ${visibleBody ? `<div class="pdf-text ${message.isDeleted ? "pdf-deleted" : ""}">${escapeHtml(visibleBody).replace(/\n/g, "<br>")}</div>` : ""}
          <div class="pdf-time">${escapeHtml(formatDateTime(message.createdAt))}</div>
        </div>
      </div>
    `;
  }

  html += `</div>`;
  container.innerHTML = html;
  return container;
};

const waitForImages = async (root) => {
  const images = Array.from(root.querySelectorAll("img"));
  await Promise.all(images.map((img) => {
    if (img.complete) return Promise.resolve();
    return new Promise((resolve) => {
      const done = () => resolve();
      img.onload = done;
      img.onerror = done;
      window.setTimeout(done, 8000);
    });
  }));
};

export default function TicketMessagesDialog({ open, handleClose, ticketId }) {
  const [exporting, setExporting] = useState(false);
  const [progressText, setProgressText] = useState("");
  const exportKeyRef = useRef("");

  const exportConversation = async () => {
    if (!ticketId || exporting) return;

    setExporting(true);
    setProgressText("Carregando a conversa completa...");

    let hiddenContainer;
    try {
      const [{ data: ticket }, { data: messageData }] = await Promise.all([
        api.get(`/tickets/${ticketId}`),
        api.get(`/messages/${ticketId}`, {
          params: { pageNumber: 1, exportAll: "true" },
        }),
      ]);

      const messages = Array.isArray(messageData?.messages) ? messageData.messages : [];
      setProgressText(`Preparando ${messages.length} mensagens e mídias...`);

      const conversationElement = await buildConversationElement({ ticket, messages });
      hiddenContainer = document.createElement("div");
      hiddenContainer.style.position = "fixed";
      hiddenContainer.style.left = "-100000px";
      hiddenContainer.style.top = "0";
      hiddenContainer.style.width = "760px";
      hiddenContainer.appendChild(conversationElement);
      document.body.appendChild(hiddenContainer);

      await waitForImages(conversationElement);

      setProgressText("Gerando PDF...");

      const options = {
        margin: [6, 5, 6, 5],
        filename: `relatorio_atendimento_${ticketId}.pdf`,
        image: { type: "jpeg", quality: 0.96 },
        html2canvas: {
          scale: 1.6,
          useCORS: true,
          allowTaint: false,
          logging: false,
          backgroundColor: "#efeae2",
        },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: { mode: ["css", "legacy"] },
      };

      await html2pdf().from(conversationElement).set(options).save();
      handleClose();
    } catch (err) {
      console.error("Erro ao exportar conversa:", err);
      toastError(err);
    } finally {
      if (hiddenContainer?.parentNode) hiddenContainer.parentNode.removeChild(hiddenContainer);
      setExporting(false);
      setProgressText("");
    }
  };

  useEffect(() => {
    if (!open) {
      exportKeyRef.current = "";
      return;
    }

    const key = `${ticketId}-${Date.now()}`;
    if (exportKeyRef.current) return;
    exportKeyRef.current = key;
    void exportConversation();
    // A exportação deve iniciar apenas uma vez por abertura do diálogo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ticketId]);

  return (
    <Dialog maxWidth="sm" fullWidth onClose={exporting ? undefined : handleClose} open={open}>
      <DialogTitle>Exportar conversa para PDF</DialogTitle>
      <DialogContent>
        <div style={{ display: "flex", alignItems: "center", gap: 12, minHeight: 52 }}>
          {exporting && <CircularProgress size={26} />}
          <Typography variant="body2">
            {progressText || "Pronto para exportar a conversa."}
          </Typography>
        </div>
      </DialogContent>
      <DialogActions>
        {!exporting && (
          <Button onClick={exportConversation} color="primary">
            Exportar para PDF
          </Button>
        )}
        <Button onClick={handleClose} color="primary" disabled={exporting}>
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
